import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, Upload, Trash2, Save, Eye, Building2, 
  Palette, Mail, Phone, Globe, Crown, AlertCircle, CreditCard,
  ExternalLink, CalendarClock, ShieldCheck, Settings2, ReceiptText, Paintbrush
} from 'lucide-react';
import { hasProAccess, PRO_BRANDING_UPGRADE_MESSAGE } from '@/utils/planMessaging';

const BrandingSettings = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const subscriptionSectionRef = useRef(null);
  const brandingSectionRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [branding, setBranding] = useState({
    company_name: '',
    logo_url: null,
    primary_color: '#f59e0b',
    secondary_color: '#1e293b',
    tagline: '',
    contact_email: '',
    contact_phone: '',
    website: ''
  });
  const [hasCustomBranding, setHasCustomBranding] = useState(false);
  const [previewLogo, setPreviewLogo] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('subscription');
  const canUseBranding = hasProAccess(user?.tier);

  useEffect(() => {
    fetchSubscription();
    if (canUseBranding) {
      fetchBranding();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canUseBranding]);

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/billing/subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscription(response.data);
    } catch (_error) {
      toast.error('Failed to load subscription details');
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const response = await axios.post(
        `${API}/billing/stripe/customer-portal`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.assign(response.data.portal_url);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Subscription management is unavailable');
      setPortalLoading(false);
    }
  };

  const formatPeriodEnd = (value) => {
    if (!value) return null;
    const date = typeof value === 'number' ? new Date(value * 1000) : new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'long' }).format(date);
  };

  const scrollToSection = (section) => {
    setActiveSection(section);
    const target = section === 'subscription' ? subscriptionSectionRef.current : brandingSectionRef.current;
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const fetchBranding = async () => {
    try {
      const response = await axios.get(`${API}/branding`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.branding) {
        setBranding(prev => ({ ...prev, ...response.data.branding }));
        setPreviewLogo(response.data.branding.logo_url || null);
      }
      setHasCustomBranding(response.data.has_custom_branding);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response?.data?.detail || PRO_BRANDING_UPGRADE_MESSAGE);
      } else {
        toast.error('Failed to load branding settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo must be less than 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target.result;
      setPreviewLogo(base64);
      setBranding(prev => ({ ...prev, logo_url: base64 }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setPreviewLogo(null);
    setBranding(prev => ({ ...prev, logo_url: null }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        company_name: branding.company_name || null,
        logo_data: branding.logo_url || null,
        primary_color: branding.primary_color,
        secondary_color: branding.secondary_color,
        tagline: branding.tagline || null,
        contact_email: branding.contact_email || null,
        contact_phone: branding.contact_phone || null,
        website: branding.website || null
      };

      await axios.put(`${API}/branding`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Branding settings saved successfully');
      setHasCustomBranding(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to remove all custom branding? Reports will use default Flowguard branding.')) {
      return;
    }

    try {
      await axios.delete(`${API}/branding`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setBranding({
        company_name: '',
        logo_url: null,
        primary_color: '#f59e0b',
        secondary_color: '#1e293b',
        tagline: '',
        contact_email: '',
        contact_phone: '',
        website: ''
      });
      setPreviewLogo(null);
      setHasCustomBranding(false);
      toast.success('Branding reset to default');
    } catch (error) {
      toast.error('Failed to reset branding');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <header className="sticky top-0 z-40 border-b border-slate-800/80 bg-slate-950/90 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="grid h-9 w-9 place-items-center rounded-lg border border-slate-800 bg-slate-900 text-slate-400 transition hover:border-slate-700 hover:text-white"
              aria-label="Back to dashboard"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div>
              <p className="text-sm font-semibold text-white">Settings</p>
              <p className="text-xs text-slate-500">Account preferences and billing</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="rounded-lg px-3 py-2 text-sm font-medium text-slate-400 transition hover:bg-slate-900 hover:text-white"
          >
            Dashboard
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
        <div className="mb-8 max-w-2xl">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-amber-500/20 bg-amber-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-amber-300">
            <Settings2 className="h-3.5 w-3.5" />
            Workspace settings
          </div>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Manage your Flowguard account</h1>
          <p className="mt-3 leading-7 text-slate-400">Review your plan, manage billing, and control how your investor-ready reports appear.</p>
        </div>

        <div className="grid gap-8 lg:grid-cols-[220px_minmax(0,1fr)]">
          <aside className="lg:sticky lg:top-24 lg:h-fit">
            <nav className="grid gap-1 rounded-2xl border border-slate-800 bg-slate-900/60 p-2" aria-label="Settings sections">
              <button
                type="button"
                onClick={() => scrollToSection('subscription')}
                className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition ${
                  activeSection === 'subscription'
                    ? 'bg-slate-800 font-semibold text-white'
                    : 'font-medium text-slate-400 hover:bg-slate-800/70 hover:text-white'
                }`}
              >
                <ReceiptText className="h-4 w-4 text-amber-400" />
                Subscription
              </button>
              <button
                type="button"
                onClick={() => scrollToSection('branding')}
                className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition ${
                  activeSection === 'branding'
                    ? 'bg-slate-800 font-semibold text-white'
                    : 'font-medium text-slate-400 hover:bg-slate-800/70 hover:text-white'
                }`}
              >
                <Paintbrush className="h-4 w-4" />
                Report branding
              </button>
            </nav>
            <p className="mt-4 px-3 text-xs leading-5 text-slate-600">Changes to report branding only affect reports generated after you save.</p>
          </aside>

          <div className="min-w-0 space-y-10">
        <section ref={subscriptionSectionRef} className="scroll-mt-28 overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60 shadow-xl shadow-black/10">
          <div className="border-b border-slate-800 px-6 py-5">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-400">Billing</p>
            <h2 className="mt-1 text-xl font-bold">Subscription</h2>
            <p className="mt-1 text-sm text-slate-400">Manage your current plan and Stripe billing details.</p>
          </div>
          <div className="p-6">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <h3 className="text-2xl font-bold capitalize">{subscription?.tier || user?.tier || 'free'} Investor</h3>
                {subscription?.status && (
                  <span className="rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-xs font-semibold capitalize text-emerald-300">
                    {subscription.status.replaceAll('_', ' ')}
                  </span>
                )}
              </div>
              <div className="mt-4 space-y-2 text-sm text-slate-400">
                {subscription?.cancel_at_period_end ? (
                  <p className="flex items-center gap-2 text-amber-300">
                    <CalendarClock className="h-4 w-4" />
                    Cancellation scheduled{formatPeriodEnd(subscription.current_period_end) ? ` for ${formatPeriodEnd(subscription.current_period_end)}` : ''}. Paid access continues until then.
                  </p>
                ) : (
                  <p className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-emerald-400" />
                    Subscription charges are non-refundable. Cancellation takes effect at the end of the paid billing period.
                  </p>
                )}
                <p>Starter subscriptions can upgrade to Pro immediately. To move from Pro to Starter, cancel Pro and wait until your account returns to Free.</p>
              </div>
            </div>

            <div className="flex shrink-0 flex-col gap-3 sm:flex-row md:flex-col">
              {subscription?.portal_available ? (
                <button
                  onClick={handleManageSubscription}
                  disabled={portalLoading}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-3 font-semibold text-black disabled:opacity-50"
                >
                  <CreditCard className="h-4 w-4" />
                  {portalLoading ? 'Opening Stripe...' : 'Manage subscription'}
                  {!portalLoading && <ExternalLink className="h-4 w-4" />}
                </button>
              ) : subscription?.tier === 'free' ? (
                <button
                  onClick={() => navigate('/upgrade')}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-3 font-semibold text-black"
                >
                  <CreditCard className="h-4 w-4" />
                  View plans
                </button>
              ) : (
                <div className="max-w-xs rounded-xl border border-slate-700 bg-slate-900/70 p-4 text-sm leading-6 text-slate-400">
                  This subscription is not managed through Stripe. Contact support for billing assistance.
                </div>
              )}
            </div>
          </div>
          <div className="mt-6 grid gap-px overflow-hidden rounded-xl border border-slate-800 bg-slate-800 sm:grid-cols-3">
            <div className="bg-slate-950/70 p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Plan</p>
              <p className="mt-1 font-semibold capitalize text-slate-200">{subscription?.tier || user?.tier || 'free'} Investor</p>
            </div>
            <div className="bg-slate-950/70 p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Billing provider</p>
              <p className="mt-1 font-semibold capitalize text-slate-200">{subscription?.provider || 'Not applicable'}</p>
            </div>
            <div className="bg-slate-950/70 p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Current period ends</p>
              <p className="mt-1 font-semibold text-slate-200">{formatPeriodEnd(subscription?.current_period_end) || 'No paid billing period'}</p>
            </div>
          </div>
          </div>
        </section>

        <div ref={brandingSectionRef} className="scroll-mt-28">
          <div className="mb-5 flex flex-col gap-4 border-b border-slate-800 pb-5 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-400">Reports</p>
              <h2 className="mt-1 text-2xl font-bold">Report branding</h2>
              <p className="mt-1 text-sm text-slate-400">Customize how your PDF reports appear to clients.</p>
            </div>
            {canUseBranding && <div className="flex gap-3">
              {hasCustomBranding && (
                <button
                  onClick={handleReset}
                  className="flex items-center gap-2 rounded-lg border border-red-500/30 px-4 py-2 text-sm font-semibold text-red-400 transition-colors hover:bg-red-500/10"
                >
                  <Trash2 className="h-4 w-4" />
                  Reset
                </button>
              )}
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-2 text-sm font-semibold text-black transition hover:shadow-lg hover:shadow-amber-500/20 disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {saving ? 'Saving...' : 'Save changes'}
              </button>
            </div>}
          </div>
        </div>

        {!canUseBranding ? (
          <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-10 text-center shadow-xl shadow-black/10">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Crown className="w-8 h-8 text-amber-500" />
            </div>
            <h2 className="text-2xl font-bold mb-3">Pro report branding</h2>
            <p className="text-slate-400 mb-6">{PRO_BRANDING_UPGRADE_MESSAGE}</p>
            <button
              onClick={() => navigate('/upgrade')}
              className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-black font-semibold rounded-lg"
            >
              Upgrade to Pro
            </button>
          </div>
        ) : <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
          {/* Left Column - Settings */}
          <div className="space-y-6">
            {/* Company Info */}
            <div className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60">
              <div className="border-b border-slate-800 px-6 py-5">
                <h3 className="flex items-center gap-2 text-base font-semibold">
                  <Building2 className="h-4 w-4 text-amber-500" />
                  Company information
                </h3>
                <p className="mt-1 text-sm text-slate-500">Details displayed at the top of branded reports.</p>
              </div>
              
              <div className="space-y-4 p-6">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Company Name</label>
                  <input
                    type="text"
                    value={branding.company_name}
                    onChange={(e) => setBranding(prev => ({ ...prev, company_name: e.target.value }))}
                    placeholder="Your Company Name"
                    className="h-11 w-full rounded-lg border border-slate-700 bg-slate-950/60 px-4 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/10"
                  />
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-1">Tagline (Optional)</label>
                  <input
                    type="text"
                    value={branding.tagline}
                    onChange={(e) => setBranding(prev => ({ ...prev, tagline: e.target.value }))}
                    placeholder="Your Professional Tagline"
                    className="h-11 w-full rounded-lg border border-slate-700 bg-slate-950/60 px-4 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/10"
                  />
                </div>
              </div>
            </div>

            {/* Logo Upload */}
            <div className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60">
              <div className="border-b border-slate-800 px-6 py-5">
                <h3 className="flex items-center gap-2 text-base font-semibold">
                  <Upload className="h-4 w-4 text-amber-500" />
                  Company logo
                </h3>
                <p className="mt-1 text-sm text-slate-500">Upload a clear logo for report headers.</p>
              </div>

              <div className="space-y-4 p-6">
                {previewLogo ? (
                  <div className="relative">
                    <div className="p-4 bg-white rounded-lg inline-block">
                      <img 
                        src={previewLogo} 
                        alt="Logo preview" 
                        className="max-h-24 max-w-full object-contain"
                      />
                    </div>
                    <button
                      onClick={handleRemoveLogo}
                      className="absolute top-2 right-2 p-1 bg-red-500 rounded-full hover:bg-red-600 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="cursor-pointer rounded-xl border border-dashed border-slate-700 bg-slate-950/30 p-8 text-center transition-colors hover:border-amber-500/50 hover:bg-amber-500/5"
                  >
                    <Upload className="w-8 h-8 mx-auto mb-2 text-slate-500" />
                    <p className="text-slate-400">Click to upload logo</p>
                    <p className="text-xs text-slate-500 mt-1">PNG, JPG up to 2MB</p>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
              </div>
            </div>

            {/* Colors */}
            <div className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60">
              <div className="border-b border-slate-800 px-6 py-5">
                <h3 className="flex items-center gap-2 text-base font-semibold">
                  <Palette className="h-4 w-4 text-amber-500" />
                  Brand colors
                </h3>
                <p className="mt-1 text-sm text-slate-500">Choose colors used for report accents and headings.</p>
              </div>

              <div className="grid gap-4 p-6 sm:grid-cols-2">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Primary Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={branding.primary_color}
                      onChange={(e) => setBranding(prev => ({ ...prev, primary_color: e.target.value }))}
                      className="w-10 h-10 rounded cursor-pointer border-0"
                    />
                    <input
                      type="text"
                      value={branding.primary_color}
                      onChange={(e) => setBranding(prev => ({ ...prev, primary_color: e.target.value }))}
                      className="min-w-0 flex-1 rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Secondary Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={branding.secondary_color}
                      onChange={(e) => setBranding(prev => ({ ...prev, secondary_color: e.target.value }))}
                      className="w-10 h-10 rounded cursor-pointer border-0"
                    />
                    <input
                      type="text"
                      value={branding.secondary_color}
                      onChange={(e) => setBranding(prev => ({ ...prev, secondary_color: e.target.value }))}
                      className="min-w-0 flex-1 rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60">
              <div className="border-b border-slate-800 px-6 py-5">
                <h3 className="flex items-center gap-2 text-base font-semibold">
                  <Mail className="h-4 w-4 text-amber-500" />
                  Contact information
                </h3>
                <p className="mt-1 text-sm text-slate-500">Optional contact details shown in report footers.</p>
              </div>

              <div className="space-y-4 p-6">
                <div>
                  <label className="block text-sm text-slate-400 mb-1 flex items-center gap-1">
                    <Mail className="w-3 h-3" /> Email
                  </label>
                  <input
                    type="email"
                    value={branding.contact_email}
                    onChange={(e) => setBranding(prev => ({ ...prev, contact_email: e.target.value }))}
                    placeholder="contact@yourcompany.com"
                    className="h-11 w-full rounded-lg border border-slate-700 bg-slate-950/60 px-4 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/10"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1 flex items-center gap-1">
                    <Phone className="w-3 h-3" /> Phone
                  </label>
                  <input
                    type="tel"
                    value={branding.contact_phone}
                    onChange={(e) => setBranding(prev => ({ ...prev, contact_phone: e.target.value }))}
                    placeholder="(555) 123-4567"
                    className="h-11 w-full rounded-lg border border-slate-700 bg-slate-950/60 px-4 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/10"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1 flex items-center gap-1">
                    <Globe className="w-3 h-3" /> Website
                  </label>
                  <input
                    type="url"
                    value={branding.website}
                    onChange={(e) => setBranding(prev => ({ ...prev, website: e.target.value }))}
                    placeholder="https://yourcompany.com"
                    className="h-11 w-full rounded-lg border border-slate-700 bg-slate-950/60 px-4 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/10"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Preview */}
          <div className="sticky top-24 h-fit rounded-2xl border border-slate-800 bg-slate-900/60 p-6 shadow-xl shadow-black/10">
            <h3 className="mb-4 flex items-center gap-2 text-base font-semibold">
              <Eye className="w-5 h-5 text-amber-500" />
              Report preview
            </h3>

            <div className="bg-white rounded-lg p-6 text-black">
              {/* Preview Header */}
              <div className="border-b-2 pb-4 mb-4" style={{ borderColor: branding.primary_color }}>
                <div className="flex items-center gap-4">
                  {previewLogo && (
                    <img 
                      src={previewLogo} 
                      alt="Logo" 
                      className="h-12 w-auto object-contain"
                    />
                  )}
                  <div>
                    <h3 
                      className="text-xl font-bold"
                      style={{ color: branding.primary_color }}
                    >
                      {branding.company_name || 'Your Company Name'}
                    </h3>
                    {branding.tagline && (
                      <p className="text-sm text-gray-500">{branding.tagline}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Preview Content */}
              <div>
                <h4 
                  className="text-lg font-bold mb-2"
                  style={{ color: branding.secondary_color }}
                >
                  Investment Analysis Report
                </h4>
                <p className="text-gray-600 text-sm">123 Sample Property Street</p>
                <p className="text-gray-400 text-xs mt-1">United States | Generated January 8, 2026</p>
              </div>

              {/* Preview Footer */}
              <div className="mt-6 pt-4 border-t text-center">
                <p 
                  className="font-semibold text-sm"
                  style={{ color: branding.primary_color }}
                >
                  {branding.company_name || 'Your Company Name'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {[branding.contact_email, branding.contact_phone, branding.website]
                    .filter(Boolean)
                    .join(' | ') || 'Your contact information will appear here'}
                </p>
              </div>
            </div>

            <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-200">
                  Your branding will appear on all PDF reports you generate. 
                  Existing reports will not be affected.
                </p>
              </div>
            </div>
          </div>
        </div>}
      </div>
        </div>
      </main>
    </div>
  );
};

export default BrandingSettings;
